import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Badge — "Operational Quiet" badge: neutral background with a colored dot
 * prefix to convey tone. Use sparingly; reach for `tone="neutral"` first.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
  {
    variants: {
      tone: {
        neutral: 'border-border bg-muted text-foreground',
        primary: 'border-primary/20 bg-primary/10 text-primary-700 dark:text-primary-200',
        accent: 'border-accent/20 bg-accent/10 text-accent-700 dark:text-accent-100',
        success: 'border-success/20 bg-success/10 text-success',
        warning: 'border-warning/20 bg-warning/10 text-warning-foreground dark:text-warning',
        destructive: 'border-destructive/20 bg-destructive/10 text-destructive',
        info: 'border-info/20 bg-info/10 text-info',
      },
      solid: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        tone: 'primary',
        solid: true,
        className: 'border-transparent bg-primary text-primary-foreground',
      },
      {
        tone: 'accent',
        solid: true,
        className: 'border-transparent bg-accent text-accent-foreground',
      },
      {
        tone: 'success',
        solid: true,
        className: 'border-transparent bg-success text-success-foreground',
      },
      {
        tone: 'warning',
        solid: true,
        className: 'border-transparent bg-warning text-warning-foreground',
      },
      {
        tone: 'destructive',
        solid: true,
        className:
          'border-transparent bg-destructive text-destructive-foreground',
      },
      {
        tone: 'info',
        solid: true,
        className: 'border-transparent bg-info text-info-foreground',
      },
    ],
    defaultVariants: {
      tone: 'neutral',
      solid: false,
    },
  },
);

const dotVariants = cva('h-1.5 w-1.5 shrink-0 rounded-full', {
  variants: {
    tone: {
      neutral: 'bg-muted-foreground',
      primary: 'bg-primary',
      accent: 'bg-accent',
      success: 'bg-success',
      warning: 'bg-warning',
      destructive: 'bg-destructive',
      info: 'bg-info',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a leading colored dot. Hidden automatically for solid badges. */
  withDot?: boolean;
}

function Badge({
  className,
  tone,
  solid,
  withDot = true,
  children,
  ...props
}: BadgeProps) {
  const showDot = withDot && !solid;
  return (
    <span className={cn(badgeVariants({ tone, solid }), className)} {...props}>
      {showDot && <span className={dotVariants({ tone })} aria-hidden />}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
